import { useCallback, useState } from "react";
import { EvgenEvent, EvgenEventsObject } from "../IEvgen";
import { IOption } from "../SendInappEventParam";

interface UseEvgenInappParams {
  isFlutter: boolean;
  isAndroid: boolean;
  isReactNative: boolean;
  responseListRequired: string;
  updateEventCodeEvgenAndroid: (
    isResponse: string,
    evgenEvents?: EvgenEvent[],
    filteredOptions?: IOption[]
  ) => void;
  updateEventCodeEvgenFlutter: (
    evgenEvents?: EvgenEvent[],
    filteredOptions?: IOption[]
  ) => void;
  updateEventCodeEvgenIOS: (
    isResponse: string,
    evgenEvents?: EvgenEvent[],
    filteredOptions?: IOption[]
  ) => void;
  updateEventCodeEvgenReactNative: (
    isResponse: string,
    evgenEvents?: EvgenEvent[],
    filteredOptions?: IOption[]
  ) => void;
}

export const useEvgenInapp = ({
  isFlutter,
  isAndroid,
  isReactNative,
  responseListRequired,
  updateEventCodeEvgenAndroid,
  updateEventCodeEvgenFlutter,
  updateEventCodeEvgenIOS,
  updateEventCodeEvgenReactNative,
}: UseEvgenInappParams) => {
  const [evgenEventsObject, setEvgenEventsObject] = useState<EvgenEventsObject>();
  const [filteredEventOptions, setFilteredEventOptions] = useState<IOption[]>([]);

  const filterEvgenEvents = useCallback(
    (filteredOptions: IOption[]) => {
      setFilteredEventOptions(filteredOptions);

      if (!evgenEventsObject?.events?.length) {
        return;
      }

      if (isAndroid) {
        updateEventCodeEvgenAndroid(
          responseListRequired,
          evgenEventsObject.events,
          filteredOptions
        );
      } else if (isReactNative) {
        updateEventCodeEvgenReactNative(
          responseListRequired,
          evgenEventsObject.events,
          filteredOptions
        );
      } else {
        updateEventCodeEvgenIOS(
          responseListRequired,
          evgenEventsObject.events,
          filteredOptions
        );
      }
    },
    [
      evgenEventsObject,
      isAndroid,
      isReactNative,
      responseListRequired,
      updateEventCodeEvgenAndroid,
      updateEventCodeEvgenIOS,
      updateEventCodeEvgenReactNative,
    ]
  );

  const handleFileUpload = useCallback(
    (newFile: File, _target?: unknown) => {
      void _target;
      if (!newFile) {
        return;
      }

      const fileReader = new FileReader();
      fileReader.readAsText(newFile, "UTF-8");
      fileReader.onload = (e) => {
        try {
          const json = JSON.parse((e?.target?.result ?? "").toString());
          if (json?.events) {
            setEvgenEventsObject(json);
            if (isFlutter) {
              updateEventCodeEvgenFlutter(json?.events);
            } else if (isAndroid) {
              updateEventCodeEvgenAndroid("No", json?.events);
            } else if (isReactNative) {
              updateEventCodeEvgenReactNative("No", json?.events);
            } else {
              updateEventCodeEvgenIOS("No", json?.events);
            }
          } else {
            console.error("JSON does not match the EVGEN output structure");
          }
        } catch (error) {
          console.error("JSON parse error: ", error);
        }
      };
    },
    [
      isAndroid,
      isFlutter,
      isReactNative,
      updateEventCodeEvgenAndroid,
      updateEventCodeEvgenFlutter,
      updateEventCodeEvgenIOS,
      updateEventCodeEvgenReactNative,
    ]
  );

  return {
    evgenEventsObject,
    filteredEventOptions,
    filterEvgenEvents,
    handleFileUpload,
  };
};
